import { ArrowLeft, Columns2, Moon, Sun } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, type RouteObject } from 'react-router-dom';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import { WorkspaceCanvas } from '@/components/layout/workspace/WorkspaceCanvas';
import { WorkspaceProvider } from '@/components/layout/workspace/WorkspaceProvider';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import type { RightPanelConfig, RightPanelItem } from '@/components/layout/workspace/rightPanelContext';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  Badge,
  Button,
  Deadline,
  FilePreview,
  IconButton,
  Money,
  PropertyList,
  PropertyRow,
  StatusControl,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ToastProvider,
  ZonedDateTime,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { AttachmentDemo } from './AttachmentDemo';
import { BoardDemo } from './BoardDemo';
import { WideTableDemo } from './WideTableDemo';
import { ScheduleDemo } from './ScheduleDemo';
import { OperationsProvider } from './OperationsProvider';
import { useOperations } from './operationsContext';
import { COLUMNS, DUTY_ROWS, HOME, SAMPLE_NOW, SHIFTS, resolveOperations } from './model';

// Каждый вид волны 2 открывается явно по demo; прежнее умолчание остаётся файлами.
const availableViews = [
  { id: 'files', title: 'Файлы', Content: AttachmentDemo },
  { id: 'board', title: 'Доска', Content: BoardDemo },
  { id: 'table', title: 'Таблица', Content: WideTableDemo },
  { id: 'schedule', title: 'Сетка времени', Content: ScheduleDemo },
];

export function OperationsPatterns() {
  const [options] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { embedded: params.has('embedded'), demo: params.get('demo') ?? 'files' };
  });
  return (
    <ToastProvider>
      <OperationsProvider>
        <WorkspaceProvider
          homePath={HOME}
          resolve={resolveOperations}
          storageKey={'kaname.operations.workspace.' + options.demo + '.v1'}
        >
          <ObserverProvider home="core" storageKey="kaname.operations.observer.v1">
            <SampleShell embedded={options.embedded} demo={options.demo} />
          </ObserverProvider>
        </WorkspaceProvider>
      </OperationsProvider>
    </ToastProvider>
  );
}
function SampleShell({ embedded, demo }: { embedded: boolean; demo: string }) {
  const routes = useMemo<RouteObject[]>(
    () => [
      { path: '/file/:id', element: <FilePage /> },
      { path: '/item/:id', element: <ItemPage /> },
      { path: '/shift/:id', element: <ShiftPage /> },
      { path: '*', element: <Examples demo={demo} /> },
    ],
    [demo],
  );
  const ws = useWorkspace();
  const { mode, toggle } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const config = useMemo<RightPanelConfig>(
    () => ({
      storageKey: 'kaname.operations.panels.' + demo + '.v1',
      render: (item) => <Details item={item} />,
    }),
    [demo],
  );
  return (
    <div className="flex h-screen flex-col bg-frame text-fg">
      {!embedded && (
        <header className="flex shrink-0 flex-wrap items-center gap-4 border-b border-line bg-canvas px-6 py-3">
          <Link
            to="/design#operation-patterns"
            className="inline-flex items-center gap-2 text-xs font-medium text-fg-muted hover:text-brand-fg"
          >
            <ArrowLeft className="size-4" />
            Галерея
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="type-subheading">Элементы рабочих процессов</h1>
            <p className="text-xs text-fg-muted">Перетаскивание · буфер · просмотр в правой панели</p>
          </div>
          <Badge>Образец</Badge>
          <Button variant="secondary" size="sm" onClick={() => ws.openBeside(HOME + '/item/042')}>
            <Columns2 className="size-4" />
            Второе окно
          </Button>
          <IconButton
            variant="secondary"
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </header>
      )}
      <div data-shell="" className="relative flex min-h-0 flex-1 gap-3 p-3">
        <MainMenu
          expanded={expanded}
          onToggleExpand={() => setExpanded((v) => !v)}
          homeActive
          onHome={() => ws.go(HOME)}
          activeId={null}
          sections={[]}
          tools={[]}
          onSelect={() => ws.go(HOME)}
        />
        <WorkspaceCanvas routes={routes} rightPanel={config} />
      </div>
    </div>
  );
}
function Examples({ demo }: { demo: string }) {
  const selectedDemo = demo === 'board' || demo === 'table' || demo === 'schedule' ? demo : 'files';
  const views = availableViews.filter((view) => view.id === selectedDemo);
  const [tab, setTab] = useState(selectedDemo);
  return (
    <main className="flex min-w-0 flex-col gap-6 p-6">
      <header>
        <p className="mb-2 type-overline text-fg-subtle">Образцы · волна 2</p>
        <h2 data-panel-default-focus="" tabIndex={-1} className="type-title focus-visible:outline-none">
          Рабочие процессы
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-fg-muted">
          Общие элементы для сервисов. Примерные данные: действия можно пробовать прямо здесь.
        </p>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Элементы волны 2">
          {views.map((view) => (
            <TabsTrigger key={view.id} value={view.id}>
              {view.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {views.map(({ id, Content }) => (
          <TabsContent key={id} value={id} forceMount className="mt-6 data-[state=inactive]:hidden">
            <Content />
          </TabsContent>
        ))}
      </Tabs>
    </main>
  );
}
function Details({ item }: { item: RightPanelItem }) {
  if (item.path.includes('/file/')) return <FileContents id={item.path.split('/').at(-1)!} />;
  if (item.path.includes('/shift/')) return <ShiftContents id={item.id} />;
  return <ItemContents id={item.id} />;
}
function FileContents({ id }: { id: string }) {
  const { files } = useOperations();
  const file = files.find((f) => f.id === id);
  return file ? (
    <FilePreview key={id} file={file} />
  ) : (
    <p className="text-sm text-fg-muted">
      Вложение недоступно после обновления образца. Прикрепи файл ещё раз.
    </p>
  );
}
function ItemContents({ id }: { id: string }) {
  const { cards, move } = useOperations();
  const card = cards.find((c) => c.id === id);
  if (!card) return <p>Запись не найдена</p>;
  return (
    <PropertyList>
      <PropertyRow label="Название">{card.title}</PropertyRow>
      <PropertyRow label="Сумма">
        <Money minorUnits={card.minorUnits} currency={card.currency} />
      </PropertyRow>
      <PropertyRow label="Срок">
        <Deadline at={card.dueAt} now={SAMPLE_NOW} />
      </PropertyRow>
      <PropertyRow label="Статус">
        <StatusControl
          value={card.columnId}
          options={COLUMNS.map((c) => ({ value: c.id, label: c.title, tone: c.tone ?? 'neutral' }))}
          onChange={(value) => move(id, value)}
        />
      </PropertyRow>
      <PropertyRow label="Вложений">{card.attachments ?? 0}</PropertyRow>
    </PropertyList>
  );
}
function ShiftContents({ id }: { id: string }) {
  const shift = SHIFTS.find((s) => s.id === id);
  if (!shift) return <p>Смена не найдена</p>;
  return (
    <PropertyList>
      <PropertyRow label="Дежурный">{DUTY_ROWS.find((r) => r.id === shift.rowId)?.title}</PropertyRow>
      <PropertyRow label="Начало">
        <ZonedDateTime at={shift.start} />
      </PropertyRow>
      <PropertyRow label="Конец">
        <ZonedDateTime at={shift.end} />
      </PropertyRow>
    </PropertyList>
  );
}
function FilePage() {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <FileContents id={id} />
    </article>
  );
}
function ItemPage() {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <h2 className="mb-6 type-title">Запись № {id}</h2>
      <ItemContents id={id} />
    </article>
  );
}
function ShiftPage() {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <h2 className="mb-6 type-title">Дежурство</h2>
      <ShiftContents id={id} />
    </article>
  );
}
